export const name = 'Timezone'

import { useMainStore } from '@/stores/main'
import * as Server from '@/services/Server'

/**
 * Timezone the dates are written in (see lib/dates).
 *
 * - the choice lives on the client (localStorage) and, for a registered user, on the server
 *   (user_settings.timezone), which is the source of truth and is applied at login;
 * - null means "automatic": the timezone the browser declares;
 * - when nobody declares one, the site's own is used — it is the one the event windows are measured
 *   in too (config/events.php on the server).
 */

/** What the server sent with the bootstrap: the site's own timezone and the ones on offer. */
function bootstrap()
{
  const store = useMainStore()

  return store.serverConfig?.timezone ?? {}
}

/** Timezones a user may pick. */
export function available()
{
  return bootstrap().available ?? []
}

/** Timezone the browser declares, or null when it declares none. */
export function detected()
{
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch (error) {
    return null
  }
}

/** Whether the server offers a timezone. An empty list (bootstrap not loaded) accepts anything. */
export function isKnown(timezone)
{
  const list = available()

  return !timezone || list.length === 0 || list.includes(timezone)
}

/** The choice made on this device: null when automatic. */
export function chosen()
{
  const store = useMainStore()

  return store.settings.timezone ?? null
}

/** The timezone the pages use when nobody chooses one: the browser's, or the site's own. */
export function automatic()
{
  return detected() ?? bootstrap().default ?? null
}

/**
 * The timezone an instant is written in: the choice, the browser's, or the site's own one. Read when
 * a date is rendered, so a change is picked up by every page without being applied anywhere.
 */
export function current()
{
  const store = useMainStore()
  const timezone = store.settings.timezone

  return timezone && isKnown(timezone) ? timezone : automatic()
}

/** Choose a timezone on this device (persisted). Null is automatic. Returns the value applied. */
export function apply(timezone)
{
  const value = timezone && isKnown(timezone) ? timezone : null
  const store = useMainStore()

  store.setProperty('timezone', value, 'settings')

  return value
}

/** Logged-in user: save the preference on the server and apply it on the client. */
export function save(timezone)
{
  const value = apply(timezone)
  const store = useMainStore()

  return Server.put(store.apiPath.user_settings, { timezone: value })
    .then(response => apply(response.data?.data?.timezone ?? value))
}

/** Logged-in user: load the preferred timezone from the server and apply it on the client. */
export function syncFromServer()
{
  const store = useMainStore()

  return Server.get(store.apiPath.user_settings)
    .then(response => apply(response.data?.data?.timezone ?? null))
}
